import { Calendar, Loader2, X } from "lucide-react"
import { Button } from "shared/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"
import { DatePicker } from "modules/permit/components/DatePicker"
import type { ChangeWorkTimeFieldErrors } from "../hooks/useChangeWorkTimeFlow"

const TIME_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, "0"))
const TIME_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) => (index * 5).toString().padStart(2, "0"))

interface ChangeWorkTimeDialogProps {
  open: boolean
  permitNumber?: string | null
  isSubmitting: boolean
  startDate: string
  endDate: string
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
  reason: string
  error?: string | null
  fieldErrors?: ChangeWorkTimeFieldErrors
  onOpenChange: (open: boolean) => void
  onStartDateChange: (value: string) => void
  onEndDateChange: (value: string) => void
  onStartHourChange: (value: string) => void
  onStartMinuteChange: (value: string) => void
  onEndHourChange: (value: string) => void
  onEndMinuteChange: (value: string) => void
  onReasonChange: (value: string) => void
  onSubmit: () => void
}

export function ChangeWorkTimeDialog({
  open,
  permitNumber,
  isSubmitting,
  startDate,
  endDate,
  startHour,
  startMinute,
  endHour,
  endMinute,
  reason,
  error,
  fieldErrors,
  onOpenChange,
  onStartDateChange,
  onEndDateChange,
  onStartHourChange,
  onStartMinuteChange,
  onEndHourChange,
  onEndMinuteChange,
  onReasonChange,
  onSubmit,
}: ChangeWorkTimeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 p-0" showCloseButton={false}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Calendar className="h-5 w-5 text-amber-500" />
              {"\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19"}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {permitNumber
                ? `${"\u0e2a\u0e33\u0e2b\u0e23\u0e31\u0e1a"} ${permitNumber}`
                : "\u0e23\u0e30\u0e1a\u0e38\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48 \u0e40\u0e27\u0e25\u0e32 \u0e41\u0e25\u0e30\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07"}
            </p>
          </DialogHeader>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
            <p>
              1. เมื่อมีการเปลี่ยนแปลงวันที่เข้าทำงานใหม่ ระบบจะส่งเอกสาร Permit เข้าอนุมัติใหม่อีกครั้ง
              และจะยังไม่สามารถเปิด Permit ได้จนกว่าจะได้รับการอนุมัติครบทุกขั้นตอน
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="change-work-start-date" className="text-sm font-semibold text-slate-900">
                {"\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"} <span className="text-rose-500">*</span>
              </Label>
              <DatePicker
                id="change-work-start-date"
                value={startDate}
                onChange={onStartDateChange}
                disabled={isSubmitting}
                placeholder="\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"
                error={fieldErrors?.startDate}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="change-work-end-date" className="text-sm font-semibold text-slate-900">
                {"\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"} <span className="text-rose-500">*</span>
              </Label>
              <DatePicker
                id="change-work-end-date"
                value={endDate}
                onChange={onEndDateChange}
                disabled={isSubmitting}
                placeholder="\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"
                error={fieldErrors?.endDate}
              />
            </div>
          </div>

          {fieldErrors?.dateRange ? <p className="text-sm text-rose-600">{fieldErrors.dateRange}</p> : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="change-work-start" className="text-sm font-semibold text-slate-900">
                {"\u0e40\u0e27\u0e25\u0e32\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"} <span className="text-rose-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Select value={startHour || undefined} onValueChange={onStartHourChange} disabled={isSubmitting}>
                  <SelectTrigger className="border-gray-200 bg-gray-50">
                    <SelectValue placeholder="\u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {TIME_HOUR_OPTIONS.map((option) => (
                      <SelectItem key={`start-hour-${option}`} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-gray-400">:</span>
                <Select
                  value={startMinute || undefined}
                  onValueChange={onStartMinuteChange}
                  disabled={isSubmitting || !startHour}
                >
                  <SelectTrigger className="border-gray-200 bg-gray-50">
                    <SelectValue placeholder="\u0e19\u0e32\u0e17\u0e35" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {TIME_MINUTE_OPTIONS.map((option) => (
                      <SelectItem key={`start-minute-${option}`} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {fieldErrors?.startTime ? <p className="text-sm text-rose-600">{fieldErrors.startTime}</p> : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="change-work-end" className="text-sm font-semibold text-slate-900">
                {"\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"} <span className="text-rose-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Select value={endHour || undefined} onValueChange={onEndHourChange} disabled={isSubmitting}>
                  <SelectTrigger className="border-gray-200 bg-gray-50">
                    <SelectValue placeholder="\u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {TIME_HOUR_OPTIONS.map((option) => (
                      <SelectItem key={`end-hour-${option}`} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-gray-400">:</span>
                <Select
                  value={endMinute || undefined}
                  onValueChange={onEndMinuteChange}
                  disabled={isSubmitting || !endHour}
                >
                  <SelectTrigger className="border-gray-200 bg-gray-50">
                    <SelectValue placeholder="\u0e19\u0e32\u0e17\u0e35" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {TIME_MINUTE_OPTIONS.map((option) => (
                      <SelectItem key={`end-minute-${option}`} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {fieldErrors?.endTime ? <p className="text-sm text-rose-600">{fieldErrors.endTime}</p> : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-work-reason" className="text-sm font-semibold text-slate-900">
              {"\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07"} <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="change-work-reason"
              rows={4}
              className={`min-h-[120px] resize-none rounded-2xl text-base placeholder:text-slate-400 ${
                fieldErrors?.reason ? "border-rose-500 focus-visible:ring-rose-500" : "border-slate-300"
              }`}
              placeholder={
                "\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e17\u0e35\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e01\u0e32\u0e23\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19 \u0e40\u0e0a\u0e48\u0e19 \u0e2a\u0e20\u0e32\u0e1e\u0e2d\u0e32\u0e01\u0e32\u0e28\u0e44\u0e21\u0e48\u0e40\u0e2d\u0e37\u0e49\u0e2d\u0e2d\u0e33\u0e19\u0e27\u0e22, \u0e40\u0e04\u0e23\u0e37\u0e48\u0e2d\u0e07\u0e21\u0e37\u0e2d\u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e1e\u0e23\u0e49\u0e2d\u0e21..."
              }
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={isSubmitting}
            />
            {fieldErrors?.reason ? <p className="text-sm text-rose-600">{fieldErrors.reason}</p> : null}
          </div>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {"\u0e22\u0e01\u0e40\u0e25\u0e34\u0e01"}
          </Button>
          <Button className="bg-amber-500 text-white hover:bg-amber-600" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calendar className="mr-2 h-4 w-4" />}
            {"\u0e22\u0e37\u0e19\u0e22\u0e31\u0e19\u0e01\u0e32\u0e23\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
